/// <amd-module name="TFS/Admin/Views/ProfileSettings/TimeAndLocaleView/TimeAndLocaleView" />
import React = require("react");

import * as AdminResources from "TFS/Admin/Views/ProfileSettings/TimeAndLocaleView/Resources";

import { IProfileUserPreferencesData } from "Contracts/DataModels";
import { TimeAndLocaleViewSource } from "Sources/TimeAndLocaleViewSource";
import { Area, Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { SetUserAttributeParameters } from "TFS/Admin/Common/Contracts/User";
import { AdminFeatureTelemetryLogger, AdminTelemetry } from "TFS/Admin/Common/Util/Telemetry/AdminTelemetry";
import { wait } from "VSS/Core/Util/Promise";
import { BearerTokenProvider } from "VSS/Platform/Authentication";
import { IVssRestClientOptions, TeamFoundationHostType } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { traceException } from "VSS/Platform/Trace";
import { Button } from "VSSUI/Button";
import { ComboBox } from "VSSUI/ComboBox";
import { Link } from "VSSUI/Link";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Page } from "VSSUI/Page";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Surface, SurfaceBackground } from "VSSUI/Surface";

// Adding props for the sake of writing L0 tests
export interface ITimeAndLocaleViewComponentProps {
    userPreferencesData: IProfileUserPreferencesData | undefined;
}

interface ITimeAndLocaleViewComponentState {
    updateInProgress?: boolean;
    error?: string;
    enableSaveBtn?: boolean;
    selectedCulture: string;
    selectedTimeZone: string;
    selectedDatePattern: string;
    selectedTimePattern: string;
}

const enum PreferenceItem {
    Culture = "TFS.Culture",
    TimeZone = "TFS.TimeZone",
    DatePattern = "TFS.DatePattern",
    TimePattern = "TFS.TimePattern"
}

interface IOptionValue {
    key?: string;
    text?: string;
}

const dataProviderId = "ms.vss-admin-web.profile-time-and-locale-view-data-provider";
const progressPollInterval = 500;
const userPrincipalId = "00000038-0000-8888-8000-000000000000";

export class TimeAndLocaleViewComponent extends VssComponent<ITimeAndLocaleViewComponentProps, ITimeAndLocaleViewComponentState> {
    private telemetryLogger: AdminTelemetry = new AdminTelemetry(this.context.pageContext);
    private cultureOptions: any[];
    private timeZoneOptions: any[];
    private datePatternOptions: any[];
    private timePatternOptions: any[];

    private cultureChanged: boolean = false;
    private timeZoneChanged: boolean = false;
    private datePatternChanged: boolean = false;
    private timePatternChanged: boolean = false;

    private userPreferencesData: IProfileUserPreferencesData;

    constructor(props: ITimeAndLocaleViewComponentProps, context: IVssComponentContext) {
        super(props, context);

        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        this.userPreferencesData = this.props.userPreferencesData || contributionService.getData<IProfileUserPreferencesData>(dataProviderId)!;

        if (this.userPreferencesData) {
            this.state = {
                updateInProgress: false,
                selectedCulture: this.userPreferencesData.culture,
                selectedTimeZone: this.userPreferencesData.timeZone,
                selectedDatePattern: this.userPreferencesData.datePattern,
                selectedTimePattern: this.userPreferencesData.timePattern
            };

            this.cultureOptions = this._getOptions(PreferenceItem.Culture);
            this.timeZoneOptions = this._getOptions(PreferenceItem.TimeZone);
            this.datePatternOptions = this._getOptions(PreferenceItem.DatePattern);
            this.timePatternOptions = this._getOptions(PreferenceItem.TimePattern);
        }
    }

    public componentDidMount(): void {
        super.componentDidMount();

        const performanceService: IVssPerformanceService = this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        performanceService.markTimeToInteractive(Area.Admin, Feature.ProfilePreferences);
    }

    public render(): JSX.Element {
        if (!this.userPreferencesData) {
            return (
                <div className="flex-column time-and-locale-view">
                    <MessageBar className="message-bar" severity={MessageBarSeverity.Error}>
                        {AdminResources.DataProviderError}
                    </MessageBar>
                </div>
            );
        }
        const subheading = (heading: string) => <h5 className="fontSizeM">{heading}</h5>;

        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="flex-grow">
                    <div className="flex-column time-and-locale-view">
                        {this.state.error ? (
                            <MessageBar
                                severity={MessageBarSeverity.Error}
                                onDismiss={() => this.setState({ error: undefined })}
                                className="message-bar"
                            >
                                {this.state.error}
                            </MessageBar>
                        ) : null}
                        <h2 className="title-l">{AdminResources.Title}</h2>
                        <div>
                            {subheading(AdminResources.PreferredLanguageLabel)}
                            <ComboBox
                                ariaLabel={AdminResources.PreferredLanguageLabel}
                                options={this.cultureOptions}
                                onChanged={(option, index, value) => {
                                    if (option) {
                                        this._onComboChanged(index!, value!, PreferenceItem.Culture);
                                    }
                                }}
                                scrollSelectedToTop={true}
                                selectedKey={this.state.selectedCulture}
                                className="option-picker"
                            />
                        </div>
                        <div>
                            {subheading(AdminResources.DatePatternLabel)}
                            <ComboBox
                                ariaLabel={AdminResources.DatePatternLabel}
                                options={this.datePatternOptions}
                                onChanged={(option, index, value) => {
                                    if (option) {
                                        this._onComboChanged(index!, value!, PreferenceItem.DatePattern);
                                    }
                                }}
                                scrollSelectedToTop={true}
                                selectedKey={this.state.selectedDatePattern}
                                className="option-picker"
                            />
                        </div>
                        <div>
                            {subheading(AdminResources.TimePatternLabel)}
                            <ComboBox
                                ariaLabel={AdminResources.TimePatternLabel}
                                options={this.timePatternOptions}
                                onChanged={(option, index, value) => {
                                    if (option) {
                                        this._onComboChanged(index!, value!, PreferenceItem.TimePattern);
                                    }
                                }}
                                scrollSelectedToTop={true}
                                selectedKey={this.state.selectedTimePattern}
                                className="option-picker"
                            />
                        </div>
                        <div>
                            {subheading(AdminResources.TimeZoneLabel)}
                            <ComboBox
                                ariaLabel={AdminResources.TimeZoneLabel}
                                options={this.timeZoneOptions}
                                onChanged={(option, index, value) => {
                                    if (option) {
                                        this._onComboChanged(index!, value!, PreferenceItem.TimeZone);
                                    }
                                }}
                                scrollSelectedToTop={true}
                                selectedKey={this.state.selectedTimeZone}
                                className="option-picker"
                            />
                        </div>
                        <div className="save-btn">
                            <Button
                                primary={true}
                                onClick={this._onSaveProfilePreferences}
                                disabled={!this.state.enableSaveBtn || this.state.updateInProgress}
                            >
                                {AdminResources.SaveButton}
                            </Button>
                            {this.state.updateInProgress ? <Spinner size={SpinnerSize.large} className="spinner" /> : null}
                        </div>
                        {this.userPreferencesData.oldProfileUrl && (
                            <div className="message-card">
                                <MessageCard severity={MessageCardSeverity.Info}>
                                    <span>
                                        {AdminResources.RedirectProfileText}
                                        &nbsp;
                                        <Link
                                            href={this.userPreferencesData.oldProfileUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            aria-label={AdminResources.RedirectProfileLinkText}
                                        >
                                            {AdminResources.RedirectProfileLinkText}
                                        </Link>
                                    </span>
                                </MessageCard>
                            </div>
                        )}
                    </div>
                </Page>
            </Surface>
        );
    }

    private _getOptions(preferenceOption: PreferenceItem): any[] {
        const comboBoxOptions = [];
        if (this.userPreferencesData) {
            let preferenceData: any[] = [];

            switch (preferenceOption) {
                case PreferenceItem.TimeZone: {
                    preferenceData = this.userPreferencesData.profilePreferences.timeZones;
                    break;
                }
                case PreferenceItem.Culture: {
                    preferenceData = this.userPreferencesData.profilePreferences.cultures;
                    break;
                }
                case PreferenceItem.DatePattern: {
                    preferenceData = this.userPreferencesData.profilePreferences.datePatterns;
                    break;
                }
                case PreferenceItem.TimePattern: {
                    preferenceData = this.userPreferencesData.profilePreferences.timePatterns;
                    break;
                }
                default: {
                    break;
                }
            }

            if (preferenceData.length > 0) {
                for (const index in preferenceData) {
                    const option: IOptionValue = this.getKeyTextAttributes(preferenceData[index], preferenceOption);
                    comboBoxOptions.push({ key: option.key, text: option.text });
                }
            }
        }
        return comboBoxOptions;
    }

    private getKeyTextAttributes(preferenceData: any, preferenceOption: PreferenceItem): IOptionValue {
        let option: IOptionValue;

        switch (preferenceOption) {
            case PreferenceItem.TimeZone: {
                option = { key: preferenceData.id, text: preferenceData.displayName };
                break;
            }
            case PreferenceItem.Culture: {
                option = { key: preferenceData.language, text: preferenceData.displayName };
                break;
            }
            case PreferenceItem.DatePattern: {
                option = { key: preferenceData.format, text: preferenceData.displayFormat };
                break;
            }
            case PreferenceItem.TimePattern: {
                option = { key: preferenceData.format, text: preferenceData.displayFormat };
                break;
            }
            default: {
                option = { key: "", text: "" };
                break;
            }
        }
        return option;
    }

    private _onComboChanged(index: number, value: string, preferenceOption: PreferenceItem) {
        if (!Number.isNaN(index)) {
            switch (preferenceOption) {
                case PreferenceItem.Culture: {
                    this.setState({ selectedCulture: this.cultureOptions[index].key });
                    this.cultureChanged = this.cultureOptions[index].key !== this.userPreferencesData.culture;
                    break;
                }
                case PreferenceItem.TimeZone: {
                    this.setState({ selectedTimeZone: this.timeZoneOptions[index].key });
                    this.timeZoneChanged = this.timeZoneOptions[index].key !== this.userPreferencesData.timeZone;
                    break;
                }
                case PreferenceItem.DatePattern: {
                    this.setState({ selectedDatePattern: this.datePatternOptions[index].key });
                    this.datePatternChanged = this.datePatternOptions[index].key !== this.userPreferencesData.datePattern;
                    break;
                }
                case PreferenceItem.TimePattern: {
                    this.setState({ selectedTimePattern: this.timePatternOptions[index].key });
                    this.timePatternChanged = this.timePatternOptions[index].key !== this.userPreferencesData.timePattern;
                    break;
                }
            }

            this._enableSaveButton(this.cultureChanged || this.timeZoneChanged || this.datePatternChanged || this.timePatternChanged);
        }
    }

    private _enableSaveButton(enable: boolean): void {
        if (this.state.enableSaveBtn !== enable) {
            this.setState({ enableSaveBtn: enable });
        }
    }

    private _onSaveProfilePreferences = async (): Promise<void> => {
        if (this.cultureChanged || this.timeZoneChanged || this.datePatternChanged || this.timePatternChanged) {
            await this._populateAndSetUserAttributes();
        }
    };

    private _populateAndSetUserAttributes = (): void => {
        const userAttributes: SetUserAttributeParameters[] = [];
        const date: Date = new Date();

        if (this.cultureChanged) {
            this._startActionCapture("Update Profile Culture", {});
            userAttributes.push({ lastModified: date, name: PreferenceItem.Culture, value: this.state.selectedCulture, revision: 0 });
        }

        if (this.datePatternChanged) {
            this._startActionCapture("Update Profile Date Pattern", {});
            userAttributes.push({ lastModified: date, name: PreferenceItem.DatePattern, value: this.state.selectedDatePattern, revision: 0 });
        }

        if (this.timePatternChanged) {
            this._startActionCapture("Update Profile Time Pattern", {});
            userAttributes.push({ lastModified: date, name: PreferenceItem.TimePattern, value: this.state.selectedTimePattern, revision: 0 });
        }

        if (this.timeZoneChanged) {
            this._startActionCapture("Update Profile TimeZone", {});
            userAttributes.push({ lastModified: date, name: PreferenceItem.TimeZone, value: this.state.selectedTimeZone, revision: 0 });
        }

        this._setUserAttributes(userAttributes);
    };

    private _setUserAttributes = async (userAttributes: SetUserAttributeParameters[]) => {
        this.setState({ updateInProgress: true });
        const source = new TimeAndLocaleViewSource(this.getRestClientOptions(userPrincipalId));

        const actionTelemetryLogger = this._startActionCapture("Update Profile Preferences", {});
        await source.setAttributes(userAttributes, this.userPreferencesData.userIdentity.subjectDescriptor, this.context).then(
            async () => {
                this._refresh();
            },
            reason => {
                this.setState({ updateInProgress: false, error: reason.message || String(reason), enableSaveBtn: true });
                actionTelemetryLogger.captureError(reason.message || String(reason));
            }
        );
    };

    private _startActionCapture = (action: string, properties: { [key: string]: any }): AdminFeatureTelemetryLogger => {
        return this.telemetryLogger.startActionCapture(Feature.ProfilePreferences, Feature.ProfilePreferences, action, properties);
    };

    private getRestClientOptions(serviceInstanceType: string): IVssRestClientOptions {
        return {
            serviceInstanceType: serviceInstanceType,
            hostType: TeamFoundationHostType.Deployment,
            authTokenProvider: this.userPreferencesData.namedSessionToken
                ? new BearerTokenProvider(this.userPreferencesData.namedSessionToken.token)
                : undefined
        };
    }

    private async _refresh(): Promise<void> {
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const actionTelemetryLogger = this._startActionCapture("Fetching new data after profile preferences update", {});
        try {
            let i = 0;
            while (this.state.updateInProgress === true && i++ < 100) {
                const newData = await contributionService.getDataAsync<IProfileUserPreferencesData>(
                    dataProviderId,
                    undefined /*RequestParameters*/,
                    true /*ignoreCache*/
                );
                if (newData) {
                    this.userPreferencesData = newData;
                    this.setState(
                        {
                            updateInProgress: false,
                            enableSaveBtn: false,
                            selectedCulture: this.userPreferencesData.culture,
                            selectedTimeZone: this.userPreferencesData.timeZone,
                            selectedDatePattern: this.userPreferencesData.datePattern,
                            selectedTimePattern: this.userPreferencesData.timePattern
                        },
                        () => this._setInitialValues()
                    );
                }
                await wait(progressPollInterval);
            }
        } catch (ex) {
            actionTelemetryLogger.captureError(ex.message);
            traceException(this.context.pageContext, Area.Admin, Feature.ProfilePreferences, ex);

            this.setState({
                error: AdminResources.WaitError,
                updateInProgress: false
            });
        }
    }

    private _setInitialValues() {
        this.cultureChanged = this.datePatternChanged = this.timePatternChanged = this.timeZoneChanged = false;
    }
}
Components.add("TimeAndLocaleView", TimeAndLocaleViewComponent);
